/**
 * Schéma → PV installation (decision 0017): the strings, the inverter, the battery and the
 * inverter's RCD, then `pvInstallationOps` draws the AC circuit on the board and the DC side
 * on a new sheet.
 */
import { useMemo, useState } from 'react';
import type { Id, Project } from '@elec/domain';
import type { Translate } from '@elec/i18n';
import { inverterProtection, type PvSpec } from '@elec/editor';
import { Dialog } from './ui.tsx';

export function PvDialog(props: {
  project: Project;
  t: Translate;
  /** The sheet open now: proposed as the board when it is one. */
  sheetId: Id;
  onClose(): void;
  onCreate(spec: Omit<PvSpec, 'sheetName' | 'circuitLabel'>): void;
}) {
  const { t, project } = props;
  const boards = useMemo(
    () =>
      Object.values(project.sheets)
        .filter(
          (s) =>
            s.kind === 'single-line' &&
            Object.values(project.elements).some((e) => e.sheetId === s.id && e.symbolId === 'iec.supply'),
        )
        .sort((a, b) => a.order - b.order),
    [project],
  );
  const [board, setBoard] = useState(boards.find((b) => b.id === props.sheetId)?.id ?? boards[0]?.id ?? '');
  const [strings, setStrings] = useState([{ modules: 10, moduleWp: 430, vocV: 0 }]);
  const [kw, setKw] = useState(5);
  const [phases, setPhases] = useState<1 | 3>(1);
  const [battery, setBattery] = useState(0);
  const [rcdType, setRcdType] = useState<'A' | 'B'>('B');
  const [sens, setSens] = useState(30);
  const [spd, setSpd] = useState(true);
  const num = (v: string, d: number) => {
    const n = Number(v.replace(',', '.'));
    return Number.isFinite(n) && n >= 0 ? n : d;
  };
  const kwp = Math.round(strings.reduce((s, x) => s + x.modules * x.moduleWp, 0) / 10) / 100;
  const prot = inverterProtection(kw || 0.1, phases);
  const setString = (i: number, patch: Partial<(typeof strings)[number]>) =>
    setStrings((ss) => ss.map((s, k) => (k === i ? { ...s, ...patch } : s)));
  return (
    <Dialog
      title={t('pv.title')}
      wide
      onClose={props.onClose}
      actions={
        <>
          <button onClick={props.onClose}>{t('dialog.cancel')}</button>
          <button
            className="primary"
            disabled={!board || !strings.length || !(kw > 0)}
            data-testid="pv-create"
            onClick={() =>
              props.onCreate({
                boardSheetId: board,
                strings: strings.map((s) => ({
                  modules: s.modules,
                  moduleWp: s.moduleWp,
                  ...(s.vocV ? { vocV: s.vocV } : {}),
                })),
                inverterKw: kw,
                phases,
                batteryKwh: battery,
                rcd: { sensitivityMa: sens, rcdType },
                dcSpd: spd,
              })
            }
          >
            {t('pv.create')}
          </button>
        </>
      }
    >
      <p className="muted">{t('pv.intro')}</p>
      {!boards.length && <p className="warn">{t('pv.noBoard')}</p>}
      <div className="form-grid">
        <div className="field">
          <label htmlFor="pv-board">{t('pv.board')}</label>
          <select id="pv-board" value={board} onChange={(e) => setBoard(e.target.value)}>
            {boards.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="pv-kw">{t('pv.inverterKw')}</label>
          <input
            id="pv-kw"
            inputMode="decimal"
            value={kw}
            onChange={(e) => setKw(num(e.target.value, 0))}
            data-testid="pv-kw"
          />
        </div>
        <div className="field">
          <label htmlFor="pv-phases">{t('pv.phases')}</label>
          <select id="pv-phases" value={phases} onChange={(e) => setPhases(Number(e.target.value) as 1 | 3)}>
            <option value={1}>{t('pv.mono')}</option>
            <option value={3}>{t('pv.tri')}</option>
          </select>
        </div>
        <div className="field">
          <label htmlFor="pv-battery">{t('pv.battery')}</label>
          <input
            id="pv-battery"
            inputMode="decimal"
            value={battery}
            onChange={(e) => setBattery(num(e.target.value, 0))}
            data-testid="pv-battery"
          />
        </div>
        <div className="field">
          <label htmlFor="pv-rcd">{t('pv.rcdType')}</label>
          <select
            id="pv-rcd"
            value={rcdType}
            onChange={(e) => setRcdType(e.target.value as 'A' | 'B')}
            data-testid="pv-rcd"
          >
            <option value="B">{t('pv.typeB')}</option>
            <option value="A">{t('pv.typeA')}</option>
          </select>
        </div>
        <div className="field">
          <label htmlFor="pv-sens">{t('pv.sensitivity')}</label>
          <select id="pv-sens" value={sens} onChange={(e) => setSens(Number(e.target.value))}>
            <option value={30}>30 mA</option>
            <option value={300}>300 mA</option>
          </select>
        </div>
      </div>
      <label className="check">
        <input type="checkbox" checked={spd} onChange={(e) => setSpd(e.target.checked)} />
        <span>{t('pv.dcSpd')}</span>
      </label>
      <h4>{t('pv.strings')}</h4>
      <div className="quote-table" tabIndex={0}>
        <table>
          <thead>
            <tr>
              <th />
              <th className="num">{t('pv.modules')}</th>
              <th className="num">{t('pv.moduleWp')}</th>
              <th className="num">{t('pv.vocV')}</th>
              <th />
            </tr>
          </thead>
          <tbody data-testid="pv-strings">
            {strings.map((s, i) => (
              <tr key={i}>
                <td>{t('pv.string', { n: i + 1 })}</td>
                <td className="num">
                  <input
                    className="narrow"
                    inputMode="numeric"
                    value={s.modules}
                    onChange={(e) => setString(i, { modules: Math.round(num(e.target.value, 1)) })}
                    aria-label={`${t('pv.string', { n: i + 1 })} — ${t('pv.modules')}`}
                  />
                </td>
                <td className="num">
                  <input
                    className="narrow"
                    inputMode="numeric"
                    value={s.moduleWp}
                    onChange={(e) => setString(i, { moduleWp: num(e.target.value, 0) })}
                    aria-label={`${t('pv.string', { n: i + 1 })} — ${t('pv.moduleWp')}`}
                  />
                </td>
                <td className="num">
                  <input
                    className="narrow"
                    inputMode="numeric"
                    value={s.vocV || ''}
                    onChange={(e) => setString(i, { vocV: num(e.target.value, 0) })}
                    aria-label={`${t('pv.string', { n: i + 1 })} — ${t('pv.vocV')}`}
                  />
                </td>
                <td>
                  {strings.length > 1 && (
                    <button
                      className="link"
                      onClick={() => setStrings((ss) => ss.filter((_, k) => k !== i))}
                      aria-label={t('pv.removeString', { n: i + 1 })}
                    >
                      ×
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button onClick={() => setStrings((ss) => [...ss, { ...ss.at(-1)! }])} data-testid="pv-add-string">
        {t('pv.addString')}
      </button>
      <p data-testid="pv-summary">
        {t('pv.summary', {
          kwp: String(kwp).replace('.', ','),
          kw,
          rating: prot.ratingA,
          section: String(prot.sectionMm2).replace('.', ','),
        })}
      </p>
    </Dialog>
  );
}
